import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/header/Header';
import Home from './components/home/Home';
import About from './components/about/About';
import Skills from './components/skills/Skills';
import ScrollUp from './components/scrollup/ScrollUp';
import Footer from './components/footer/Footer';
import Contact from './components/contact/Contact';
import ThreeBackground from './components/background/ThreeBackground';
import CursorTrail from './components/cursor/CursorTrail';
import { ThemeProvider } from './context/ThemeContext';
import Projects from './components/projects/Projects';
import Experience from './components/experience/Experience';
import ProjectRoute from './components/projects/ProjectRoute';
import Blogs from './components/blogs/Blogs';
import BlogRoute from './components/blogs/BlogRoute';

// Maps route pathnames to the section id that should be scrolled into view.
// `/education` lands on the Experience section (education is a tab there).
const PATH_TO_SECTION: Record<string, string> = {
  '/about': 'about',
  '/skills': 'skills',
  '/experience': 'experience',
  '/education': 'experience',
  '/projects': 'projects',
  '/blogs': 'blogs',
  '/contact': 'contact',
};

const ScrollToSection = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const sectionId = PATH_TO_SECTION[pathname];
    if (sectionId) {
      requestAnimationFrame(() => {
        const target = document.getElementById(sectionId);
        if (!target) return;
        const headerEl = document.querySelector('.header') as HTMLElement | null;
        const headerHeight = headerEl ? headerEl.offsetHeight : 0;
        window.scrollTo({ top: target.offsetTop - headerHeight, behavior: 'auto' });
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
};

const HomeLayout = () => (
  <>
    <Header />
    <main className="main">
      <Home />
      <About />
      <Skills />
      <Experience />
      <Projects />
      <Blogs />
      <Contact />
    </main>
    <Footer />
    <ScrollUp />
  </>
);

function App() {
  useEffect(() => {
    const targets = document.querySelectorAll<HTMLElement>(
      '.section:not(#home):not(#experience):not(#projects)'
    );
    targets.forEach(el => el.classList.add('section-reveal'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('section-reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    targets.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <ThemeProvider>
      <Router>
        <ThreeBackground />
        <CursorTrail />
        <ScrollToSection />

        <Routes>
          <Route path="/projects/:slug" element={<ProjectRoute />} />
          <Route path="/blogs/:slug" element={<BlogRoute />} />
          <Route path="/" element={<HomeLayout />} />
          <Route path="/about" element={<HomeLayout />} />
          <Route path="/skills" element={<HomeLayout />} />
          <Route path="/experience" element={<HomeLayout />} />
          <Route path="/education" element={<HomeLayout />} />
          <Route path="/projects" element={<HomeLayout />} />
          <Route path="/blogs" element={<HomeLayout />} />
          <Route path="/contact" element={<HomeLayout />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
